import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import {Button} from 'src/design/atoms/Button';
import {useGetAuditLogListV2Query} from 'src/redux/queries/auditLog';
import {AuditLogsList} from '../AuditLogsList';
import {DateRangeFilter} from './components/DateRangeFilter/DateRangeFilter';
import {EntityFieldSelect} from './components/EntityFieldSelect';
import {EntityTypeSelect} from './components/EntityTypeSelect';
import {SearchInput} from './components/SearchInput';

const maxPageRows = 10;

interface SearchParams {
  entityType: string;
  entityField: string;
  searchValue: string;
  startDate: Date | null;
  endDate: Date | null;
}

// Default to 30 days ago for the start date
const getDefaultStartDate = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
};

export const AuditLogsDeepSearch: React.FC = () => {
  const [selectedEntityType, setSelectedEntityType] = useState<string>('');
  const [selectedEntityField, setSelectedEntityField] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(
    getDefaultStartDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchParams | null>(null);

  const handleEntityTypeChange = (value: string): void => {
    setSelectedEntityType(value);
    // Reset the field selection when entity type changes
    setSelectedEntityField('');
    // Reset search state
    setIsSearching(false);
  };

  const handleSearch = (): void => {
    if (selectedEntityType && selectedEntityField && searchValue) {
      // Store the current search parameters
      setSearchParams({
        entityType: selectedEntityType,
        entityField: selectedEntityField,
        searchValue: searchValue,
        startDate: startDate,
        endDate: endDate,
      });
      setIsSearching(true);
      setCurrentPage(1);
    }
  };

  const {
    data,
    isLoading: isLoadin,
    isFetching,
  } = useGetAuditLogListV2Query(
    {
      start: (currentPage - 1) * maxPageRows,
      length: maxPageRows,
      filters: [
        {
          id: 'EntityName',
          values: [searchParams?.entityType || ''],
        },
      ],
      filterFns: [
        {
          id: 'EntityName',
          value: 'equals',
        },
      ],
      sorting: [{id: 'id', desc: true}],
      changesField: searchParams?.entityField || '',
      changesFieldValue: searchParams?.searchValue || '',
      startDate: searchParams?.startDate || undefined,
      endDate: searchParams?.endDate || undefined,
    },
    {
      skip: !isSearching || !searchParams,
      refetchOnMountOrArgChange: true,
    }
  );

  const isLoading = isLoadin || isFetching;
  const rows = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  return (
    <Box>
      <Flex direction="column" gap="md">
        <Stack w={{xs: '100%', sm: '400px'}}>
          <EntityTypeSelect
            value={selectedEntityType}
            onChange={handleEntityTypeChange}
          />
          <EntityFieldSelect
            entityType={selectedEntityType}
            value={selectedEntityField}
            onChange={setSelectedEntityField}
            disabled={!selectedEntityType}
          />
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            disabled={!selectedEntityField}
          />
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <Button
            onClick={handleSearch}
            disabled={
              !selectedEntityType || !selectedEntityField || !searchValue
            }>
            Search
          </Button>
        </Stack>

        {isSearching && searchParams && (
          <Box mt="md">
            <AuditLogsList
              rows={rows}
              totalRecords={totalRecords}
              isLoading={isLoading}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              maxPageRows={maxPageRows}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
